import { get, getProperties, set, setProperties } from '@ember/object';
import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import RSVP from 'rsvp';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import coachingReportValidation from 'donor-elf-web/validations/coaching/report';
import moment from 'moment';
const NUMBER_TYPES = {
  number: true,
  amount: true,
  'number&amount': true,
  'pledged&received': true,
};

// how many minutes to refresh the individual token
const REFRESH_TOKEN_MINUTES = 45;

export default Service.extend({
  store: service(),

  selectedIndividualId: null,
  selectedIndividualToken: '',
  selectedIndividualTokenExpiresAt: null,

  refreshSelectedIndividualToken() {
    let selectedIndividualId = this.selectedIndividualId;
    if (selectedIndividualId == null) {
      throw "'selectedIndividualId' property must be set before calling refreshSelectedIndividualToken()";
    }
    return this.store
      .findRecord('coaching/selected-individual-token', selectedIndividualId)
      .then((record) => {
        let token = get(record, 'token');
        if (token == null) {
          token = '';
        }
        setProperties(this, {
          selectedIndividualToken: token,
          selectedIndividualTokenExpiresAt: moment().add(
            REFRESH_TOKEN_MINUTES,
            'minutes',
          ),
        });
      });
  },

  clearSelectedIndividual() {
    setProperties(this, {
      selectedIndividualId: null,
      selectedIndividualToken: '',
      selectedIndividualTokenExpiresAt: null,
    });
  },

  confirmValidIndividualToken() {
    let tokenExpiresAt = this.selectedIndividualTokenExpiresAt;

    if (isEmpty(tokenExpiresAt) || moment() > tokenExpiresAt) {
      return this.refreshSelectedIndividualToken();
    } else {
      return RSVP.resolve();
    }
  },

  addReport(coachingIndividual) {
    let { startDate, frequencyTimespan, frequencyNumber } = getProperties(
      coachingIndividual,
      'startDate',
      'frequencyTimespan',
      'frequencyNumber',
    );
    let lastReport = get(coachingIndividual, 'sortedReportsDesc.firstObject');

    let nextReportStartDate = moment(startDate).toDate();
    if (lastReport) {
      nextReportStartDate = moment(get(lastReport, 'endDate'))
        .add(1, 'day')
        .toDate();
    }
    let nextReportEndDate = this.reportEndDate({
      startDate: nextReportStartDate,
      frequencyNumber,
      frequencyTimespan,
    });

    let report = this.store.createRecord('coaching/report', {
      individual: coachingIndividual,
      startDate: nextReportStartDate,
      endDate: nextReportEndDate,
      sectionFieldValues: {},
    });
    return report;
  },

  reportEndDate({ startDate, frequencyNumber, frequencyTimespan }) {
    return moment(startDate)
      .add(frequencyNumber, frequencyTimespan)
      .subtract(1, 'day')
      .toDate();
  },

  // on 'number&amount' field types, you can retrieve the number value
  // like this: getFieldValue(field, report, { propertyName: 'n' })
  getFieldValue(field, report, options = { propertyName: 'v' }) {
    if (isEmpty(options.propertyName)) {
      options.propertyName = 'v';
    }
    let val = get(
      report,
      `sectionFieldValues.${field.id}.${options.propertyName}`,
    );
    if (isEmpty(val) && field.t !== 'tasks') {
      return null;
    }
    if (NUMBER_TYPES[field.t]) {
      return Number(val);
    }
    if (field.t === 'response') {
      let response = {
        yes: Number(val.yes || 0),
        no: Number(val.no || 0),
        maybe: Number(val.maybe || 0),
      };
      response.total = response.yes + response.no + response.maybe;
      return response;
    }
    return val;
  },

  getFieldValueDiff(field, report, individual, { propertyName }) {
    let reports = get(individual, 'sortedReports').toArray();
    let reportIndex = reports.indexOf(report);
    let prevReport = reports[reportIndex - 1];
    let prevVal = 0;
    if (prevReport) {
      prevVal = this.getFieldValue(field, prevReport, { propertyName }) || 0;
      if (prevVal === 0) {
        for (let i = reportIndex - 1; i >= 0; i--) {
          prevVal =
            this.getFieldValue(field, reports[i], { propertyName }) || 0;
          if (prevVal > 0) {
            i = -1;
          }
        }
      }
    }
    let val = this.getFieldValue(field, report, { propertyName });
    if (isEmpty(val)) {
      return null;
    } else if (val === 0) {
      return 0;
    }
    return val - prevVal;
  },

  createChangesetForReport(individual, report) {
    let validationInstance = coachingReportValidation({ individual });
    let changeset = new Changeset(
      report,
      lookupValidator(validationInstance),
      validationInstance,
      { skipValidate: true },
    );

    // have to stringify and then parse sectionFieldValues to account for non-primitive types like arrays
    let sectionFieldValues = get(report, 'sectionFieldValues');
    if (!isEmpty(sectionFieldValues)) {
      set(
        changeset,
        'sectionFieldValues',
        JSON.parse(JSON.stringify(sectionFieldValues)),
      );
    }

    get(individual, 'sections').forEach((section) => {
      section.fields.forEach((field) => {
        if (get(report, `sectionFieldValues.${field.id}`) == null) {
          if (field.t === 'tasks') {
            set(changeset, `sectionFieldValues.${field.id}`, { v: [] });
          } else if (field.t === 'response') {
            set(changeset, `sectionFieldValues.${field.id}`, { v: {} });
          } else {
            set(changeset, `sectionFieldValues.${field.id}`, { v: '' });
          }
        }
      });
    });
    return changeset;
  },

  overallGoalTypes: {
    'pledged&received': true,
  },
  intervalGoalTypes: {
    number: true,
    amount: true,
    response: true,
  },

  calculateIntervalGoal({ individualRecord, overallGoal }) {
    return Math.ceil(
      Number(overallGoal) / get(individualRecord, 'reportingPeriodsToGoalDate'),
    );
  },
});
